import type { BrowserTrustedDeviceProofOptions } from "../../../core/account/services/browser-trusted-device.service.ts";
import { createBrowserTrustedDeviceProof } from "../../../core/account/services/browser-trusted-device.service.ts";
import { getBrowserTrustedDeviceFailureKind } from "../../../core/account/services/browser-trusted-device-error.ts";
import type { ExtractableTrustedDeviceProofOptions } from "../../../core/account/services/extractable-trusted-device.service.ts";
import { createExtractableTrustedDeviceProof } from "../../../core/account/services/extractable-trusted-device.service.ts";
import type {
  LoginTrustedDevice,
  TrustedDeviceChallenge,
  TrustedDeviceProof,
} from "../../../core/account/types";
import {
  getSafeLoginErrorDiagnostic,
  logLoginDiagnostic,
} from "./login-diagnostics.ts";
import {
  getTrustedDeviceEnvironment,
  rememberDeviceCookieMode,
} from "./trusted-device-environment.ts";
import type {
  TrustedDeviceEnvironment,
  TrustedDeviceRung,
} from "./trusted-device-fallback.ts";
import {
  initialTrustedDeviceRung,
  mayCreateStrongKey,
  nextTrustedDeviceRung,
  resolveBrowserHelpReason,
} from "./trusted-device-fallback.ts";
import type { TrustedDeviceChallengeState } from "./trusted-device-preparation.ts";
import {
  classifyTrustedDeviceFailure,
  createTrustedDevicePhaseGuard,
  getTrustedDeviceTimeoutPhase,
  isTrustedDeviceTimeoutError,
  TrustedDevicePreparationError,
} from "./trusted-device-preparation.ts";
import type {
  TrustedDeviceFailureReport,
  TrustedDeviceProbe,
} from "./trusted-device-telemetry.ts";
import {
  collectTrustedDeviceProbe,
  reportTrustedDeviceFailure,
} from "./trusted-device-telemetry.ts";

// IndexedDB, key generation and signing are local; the challenge is a network
// round-trip and gets its own, longer budget.
const localPreparationTimeout = 8_000;
const challengePreparationTimeout = 25_000;

/**
 * Placeholder `deviceId` for the cookie rung; the backend replaces it with
 * the cookie installation id it authorizes.
 */
export const DEVICE_COOKIE_DEVICE_ID = "web-device-cookie";

// Local failures that mean "this rung cannot work here" (as opposed to a
// transient stall or a network error).
const exhaustingFailures = new Set([
  "storageUnavailable",
  "keyNotStorable",
  "storageQuota",
  "keyUnsupported",
  "signingFailed",
  "notEnrolled",
]);

type SigningRung = Exclude<TrustedDeviceRung, "deviceCookie">;

type ChallengeResult = {
  isOK: boolean;
  error?: string;
  data: TrustedDeviceChallenge;
};

/**
 * Device fields merged into a login, register, or OTP request.
 *
 * @example
 * Object.assign(values, await prepareTrustedLoginDevice({ requestChallenge }));
 */
export type PreparedLoginDevice = {
  deviceId: string;
  trustedDevice: LoginTrustedDevice;
};

/**
 * Collaborators of {@link prepareTrustedLoginDevice}; everything but the
 * challenge request has a production default and is injectable for tests.
 *
 * @example
 * await prepareTrustedLoginDevice({ requestChallenge: (body) => action(body) });
 */
export type TrustedLoginDeviceDependencies = {
  requestChallenge: (body: { installationId: string }) => Promise<ChallengeResult>;
  environment?: TrustedDeviceEnvironment;
  createStrongProof?: (
    requestChallenge: (installationId: string) => Promise<TrustedDeviceChallenge>,
    options: BrowserTrustedDeviceProofOptions,
  ) => Promise<TrustedDeviceProof>;
  createExtractableProof?: (
    requestChallenge: (installationId: string) => Promise<TrustedDeviceChallenge>,
    options: ExtractableTrustedDeviceProofOptions,
  ) => Promise<TrustedDeviceProof>;
  reportFailure?: (report: TrustedDeviceFailureReport) => Promise<void>;
  probe?: () => Promise<TrustedDeviceProbe>;
  storage?: Storage;
};

function readLocalStorage() {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

// The advice only needs the private-mode hint; never hold the student's
// error message hostage to a slow probe.
const adviceProbeBudgetMs = 2_500;

function waitForProbe(
  probe: () => Promise<TrustedDeviceProbe>,
): Promise<TrustedDeviceProbe | undefined> {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => resolve(undefined), adviceProbeBudgetMs);

    probe().then(
      (result) => {
        clearTimeout(timeoutId);
        resolve(result);
      },
      () => {
        clearTimeout(timeoutId);
        resolve(undefined);
      },
    );
  });
}

function readPlatformName() {
  try {
    return navigator.platform || "Web browser";
  } catch {
    return "Web browser";
  }
}

/**
 * Prepare automatic trusted-browser enrollment or proof during login.
 *
 * @remarks
 * Walks the trusted-device ladder, strongest first:
 * 1. `webCrypto` — non-extractable ECDSA key in IndexedDB (default; a
 *    browser that supports it is never moved off it).
 * 2. `webCryptoExtractable` — same signature proof, key kept as a JWK in
 *    localStorage, only when IndexedDB cannot hold a `CryptoKey`.
 * 3. `deviceCookie` — the Remix server proves the browser with an HttpOnly
 *    cookie secret, only when the browser cannot sign at all.
 *
 * Every rung enrolls a device that counts toward the student's device limit
 * on the backend. Transient failures (another tab, timeouts, network) never
 * trigger a fallback, so they cannot silently create extra devices. When no
 * rung can work, the thrown error carries `browserHelp` advice.
 *
 * @example
 * const device = await prepareTrustedLoginDevice({ requestChallenge });
 */
export async function prepareTrustedLoginDevice(
dependencies: TrustedLoginDeviceDependencies,
): Promise<PreparedLoginDevice> {
  const {
    requestChallenge,
    createStrongProof = createBrowserTrustedDeviceProof,
    createExtractableProof = createExtractableTrustedDeviceProof,
    reportFailure = reportTrustedDeviceFailure,
    probe = collectTrustedDeviceProbe,
    storage = readLocalStorage(),
  } = dependencies;
  const environment =
    dependencies.environment ?? getTrustedDeviceEnvironment();
  const startedAt = Date.now();

  let challengeState: TrustedDeviceChallengeState = "notRequested";

  // One attempt; the abort signal makes an expired attempt drop its late
  // IndexedDB open and stop before writing keys or signing.
  const attemptProof = async (rung: SigningRung) => {
    const abortController = new AbortController();
    const guard = createTrustedDevicePhaseGuard({
      local: localPreparationTimeout,
      challenge: challengePreparationTimeout,
    });

    const challengeRequester = async (installationId: string) => {
      challengeState = "requested";
      guard.enter("challenge");

      const result = await requestChallenge({ installationId });

      if (!result.isOK) {
        throw new TrustedDevicePreparationError(
          result.error || "Trusted-device challenge request failed",
          "trustedDeviceChallenge",
        );
      }

      challengeState = "completed";
      guard.enter("local");

      return result.data;
    };

    try {
      const createProof: Promise<TrustedDeviceProof> =
        rung === "webCrypto"
          ? createStrongProof(challengeRequester, {
              signal: abortController.signal,
              allowCreate: mayCreateStrongKey(environment),
            })
          : createExtractableProof(challengeRequester, {
              signal: abortController.signal,
            });

      // The abandoned proof may still settle later; keep that from surfacing.
      createProof.catch(() => undefined);

      return await Promise.race([createProof, guard.expiration]);
    } catch (error) {
      abortController.abort();

      throw error;
    } finally {
      guard.stop();
    }
  };

  const attemptWithLocalRetry = async (rung: SigningRung) => {
    try {
      return await attemptProof(rung);
    } catch (error) {
      // A stalled local open may be a stale connection: retry once, but only
      // before a challenge was requested so no backend challenge is reused.
      const canRetry =
        getTrustedDeviceTimeoutPhase(error) === "local" &&
        challengeState === "notRequested";

      if (!canRetry) throw error;

      logLoginDiagnostic("trusted_device_local_retry", {
        rung,
        durationMs: Date.now() - startedAt,
        webCrypto: environment.webCrypto,
        indexedDb: environment.indexedDb,
      });

      return attemptProof(rung);
    }
  };

  let rung = initialTrustedDeviceRung(environment);

  try {
    while (rung && rung !== "deviceCookie") {
      try {
        const trustedDevice = await attemptWithLocalRetry(rung);

        return { deviceId: trustedDevice.installationId, trustedDevice };
      } catch (error) {
        const next = isTrustedDeviceTimeoutError(error)
          ? undefined
          : nextTrustedDeviceRung(
              rung,
              getBrowserTrustedDeviceFailureKind(error),
              environment,
            );

        if (!next) throw error;

        const stage = classifyTrustedDeviceFailure(error, challengeState);

        logLoginDiagnostic("trusted_device_fallback", {
          stage,
          from: rung,
          to: next,
          ...getSafeLoginErrorDiagnostic(error),
        });
        void reportFailure({
          stage,
          error,
          fallback: next,
          challengeState,
        });

        challengeState = "notRequested";
        rung = next;
      }
    }

    if (rung === "deviceCookie") {
      rememberDeviceCookieMode(storage);

      return {
        deviceId: DEVICE_COOKIE_DEVICE_ID,
        trustedDevice: {
          name: readPlatformName(),
          platform: "web",
          verificationMethod: "deviceCookie",
        },
      };
    }

    throw new TrustedDevicePreparationError(
      "No trusted-device method works in this browser",
      "trustedDeviceCapability",
    );
  } catch (error) {
    const stage = classifyTrustedDeviceFailure(error, challengeState);
    const failureKind = getBrowserTrustedDeviceFailureKind(error);
    const ladderExhausted =
      rung === undefined ||
      (failureKind !== undefined && exhaustingFailures.has(failureKind));
    const timedOut = isTrustedDeviceTimeoutError(error);
    const timeoutPhase = getTrustedDeviceTimeoutPhase(error);

    logLoginDiagnostic(
      "trusted_device_preparation_failed",
      {
        stage,
        rung: rung ?? "none",
        challengeState,
        timedOut,
        timeoutPhase,
        durationMs: Date.now() - startedAt,
        webCrypto: environment.webCrypto,
        indexedDb: environment.indexedDb,
        cookiesEnabled: environment.cookiesEnabled,
        inAppBrowser: environment.inAppBrowser,
        ...getSafeLoginErrorDiagnostic(error),
      },
      "error",
    );
    void reportFailure({
      stage,
      error,
      fallback: "none",
      timedOut,
      timeoutPhase,
      challengeState,
    });

    if (ladderExhausted) {
      const probeResult = await waitForProbe(probe);

      throw new TrustedDevicePreparationError(
        error instanceof Error
          ? error.message
          : "Trusted-device preparation failed",
        stage,
        {
          cause: error,
          browserHelp: resolveBrowserHelpReason(
            environment,
            probeResult?.privateModeSuspected === true,
          ),
        },
      );
    }

    if (error instanceof TrustedDevicePreparationError) {
      throw error;
    }

    throw new TrustedDevicePreparationError(
      error instanceof Error
        ? error.message
        : "Trusted-device preparation failed",
      stage,
      { cause: error },
    );
  }
}
